import { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Navbar, Nav, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ChatBot } from '../components/ChatBot';
import { api } from '../services/api';

interface Article {
  id: string
  title: string
  excerpt: string
  slug: string
  views: number
  helpful: number
  category: {
    name: string
    icon?: string
  } | null
}

export const LandingPage = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedArticles();
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-animate-section') || entry.target.id;
            if (sectionId) {
              setVisibleSections((prev) => new Set(prev).add(sectionId));
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    // Observe all sections
    const sections = document.querySelectorAll('[data-animate-section]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const fetchFeaturedArticles = async () => {
    try {
      const response = await api.get('/articles', { params: { pageSize: 6 } });
      setArticles(response.data.data || []);
    } catch (error: any) {
      // Handle connection errors gracefully - don't break the page if API is unavailable
      if (error.code === 'ERR_NETWORK' || error.message?.includes('ERR_CONNECTION_REFUSED')) {
        console.warn('API server is not available. Articles will not be displayed.');
        // Silently fail - articles section will show "No articles available yet"
      } else {
        console.error('Failed to fetch articles:', error);
      }
    }
  };

  const features = [
    {
      icon: 'fas fa-ticket-alt',
      title: 'Submit Support Tickets',
      description: 'Easily create and submit support requests with detailed descriptions and file attachments.',
      color: 'primary'
    },
    {
      icon: 'fas fa-list-check',
      title: 'Track Your Requests',
      description: 'Monitor the status of your support tickets and see real-time updates from our team.',
      color: 'primary'
    },
    {
      icon: 'fas fa-bell',
      title: 'Instant Notifications',
      description: 'Get notified immediately when your tickets are updated, assigned, or resolved.',
      color: 'primary'
    },
    {
      icon: 'fas fa-comments',
      title: 'Direct Communication',
      description: 'Communicate directly with our support team through ticket comments and messages.',
      color: 'primary'
    },
    {
      icon: 'fas fa-history',
      title: 'Request History',
      description: 'View your complete support history and access previous ticket resolutions.',
      color: 'primary'
    },
    {
      icon: 'fas fa-headset',
      title: 'Expert Support',
      description: 'Get help from our experienced support team who understand your needs.',
      color: 'primary'
    }
  ];

  return (
    <div className="landing-page">
      {/* Top Navigation */}
      <Navbar bg="white" expand="lg" className="shadow-sm border-bottom">
        <Container>
          <Navbar.Brand 
            className="fw-bold text-primary"
            style={{ cursor: 'pointer' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <i className="fas fa-headset me-2"></i>
            AACore Helpdesk
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link 
                href="#features" 
                className="fw-medium"
                onClick={(e) => {
                  e.preventDefault();
                  const featuresSection = document.getElementById('features');
                  if (featuresSection) {
                    featuresSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <i className="fas fa-star me-1"></i>
                Features
              </Nav.Link>
              <Nav.Link 
                href="#how-it-works" 
                className="fw-medium"
                onClick={(e) => {
                  e.preventDefault();
                  const section = document.getElementById('how-it-works');
                  if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <i className="fas fa-gears me-1"></i>
                How It Works
              </Nav.Link>
              <Nav.Link 
                href="#articles" 
                className="fw-medium"
                onClick={(e) => {
                  e.preventDefault();
                  const articlesSection = document.getElementById('articles');
                  if (articlesSection) {
                    articlesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <i className="fas fa-book me-1"></i>
                Articles
              </Nav.Link>
              <Nav.Link 
                href="#contact" 
                className="fw-medium"
                onClick={(e) => {
                  e.preventDefault();
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    navigate('/contact-support');
                  }
                }}
              >
                <i className="fas fa-envelope me-1"></i>
                Contact
              </Nav.Link>
              <Nav.Link
                href="https://aainomatic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="fw-medium"
              >
                <i className="fas fa-arrow-up-right-from-square me-1"></i>
                AAInomatic
              </Nav.Link>
              <Nav.Link
                href="https://aacore.net"
                target="_blank"
                rel="noopener noreferrer"
                className="fw-medium"
              >
                <i className="fas fa-arrow-up-right-from-square me-1"></i>
                AACore.net
              </Nav.Link>
              <Button 
                variant="primary" 
                className="ms-3"
                onClick={() => navigate('/login')}
              >
                <i className="fas fa-sign-in-alt me-2"></i>
                Login
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section className="hero-section py-4">
        <Container>
          <Row className="align-items-center" style={{ minHeight: '180px' }}>
            <Col lg={12} className="text-center">
              <div className="hero-content">
                <Badge bg="light" className="mb-3 px-3 py-2 text-dark border">
                  <i className="fas fa-headset me-1"></i>
                  Client Support Portal
                </Badge>
                <h1 className="display-4 fw-bold text-white mb-4">
                  <span className="d-block d-md-inline">Professional</span>
                  <span className="d-block d-md-inline"> Helpdesk System</span>
                </h1>
                <p className="lead text-white-50 mb-4">
                  Welcome to your dedicated support portal. Submit tickets, track your requests, 
                  and get help from our support team whenever you need assistance.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 mb-4 justify-content-center">
                  <Button 
                    variant="outline-light" 
                    size="lg" 
                    className="px-4 py-3"
                    onClick={() => {
                      const featuresSection = document.getElementById('features');
                      if (featuresSection) {
                        featuresSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    <i className="fas fa-info-circle me-2"></i>
                    View Features
                  </Button>
                </div>
                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3 gap-sm-4">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-check-circle text-white me-2"></i>
                    <span className="text-white-50">24/7 Support</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-check-circle text-white me-2"></i>
                    <span className="text-white-50">Fast Response</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section 
        id="features" 
        data-animate-section="features"
        className={`features-section py-5 bg-white fade-in-section ${
          visibleSections.has('features') ? 'fade-in-visible' : ''
        }`}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-dark mb-4 section-title">
                Your Support Portal Features
              </h2>
              <p className="lead text-muted section-subtitle">
                Access all the tools you need to get help, submit requests, and track your support tickets 
                in one convenient location.
              </p>
            </Col>
          </Row>
          <Row>
            {features.map((feature, index) => (
              <Col lg={4} md={6} sm={12} key={index} className="mb-4">
                <Card 
                  className={`feature-card h-100 border-0 shadow-sm fade-in-item ${
                    activeFeature === index ? 'feature-active' : ''
                  } ${visibleSections.has('features') ? 'fade-in-visible' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setActiveFeature(index)}
                >
                  <Card.Body className="p-4 text-center">
                    <div className="bg-light rounded-3 p-3 mb-3 d-inline-block icon-container">
                      <i className={`${feature.icon} fs-2 text-primary`}></i>
                    </div>
                    <h5 className="fw-bold text-dark mb-3">{feature.title}</h5>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section 
        id="how-it-works" 
        data-animate-section="how-it-works"
        className={`how-it-works-section py-5 bg-light fade-in-section ${
          visibleSections.has('how-it-works') ? 'fade-in-visible' : ''
        }`}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-dark mb-4 section-title">
                How It Works
              </h2>
              <p className="lead text-muted section-subtitle">
                Get the help you need in three simple steps
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col lg={4} md={4} className="text-center">
              <div className={`step-card h-100 p-4 fade-in-item ${visibleSections.has('how-it-works') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.1s' }}>
                <div className="step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-4" 
                     style={{ width: '80px', height: '80px', fontSize: '2rem', fontWeight: 'bold' }}>
                  1
                </div>
                <h4 className="fw-bold mb-3">Submit Your Request</h4>
                <p className="text-muted">
                  Create a ticket with a clear description of your issue. Attach files, screenshots, or any relevant documentation to help us understand your needs quickly.
                </p>
                <div className="mt-4">
                  <i className="fas fa-ticket-alt fa-2x text-primary"></i>
                </div>
              </div>
            </Col>
            <Col lg={4} md={4} className="text-center">
              <div className={`step-card h-100 p-4 fade-in-item ${visibleSections.has('how-it-works') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.2s' }}>
                <div className="step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-4" 
                     style={{ width: '80px', height: '80px', fontSize: '2rem', fontWeight: 'bold' }}>
                  2
                </div>
                <h4 className="fw-bold mb-3">Track Progress</h4>
                <p className="text-muted">
                  Monitor your ticket status in real-time. Get instant notifications when your request is assigned, updated, or resolved. View the complete history of interactions.
                </p>
                <div className="mt-4">
                  <i className="fas fa-chart-line fa-2x text-primary"></i>
                </div>
              </div>
            </Col>
            <Col lg={4} md={4} className="text-center">
              <div className={`step-card h-100 p-4 fade-in-item ${visibleSections.has('how-it-works') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.3s' }}>
                <div className="step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-4" 
                     style={{ width: '80px', height: '80px', fontSize: '2rem', fontWeight: 'bold' }}>
                  3
                </div>
                <h4 className="fw-bold mb-3">Get Resolved</h4>
                <p className="text-muted">
                  Receive expert assistance from our support team. Communicate directly through comments and receive comprehensive solutions tailored to your needs.
                </p>
                <div className="mt-4">
                  <i className="fas fa-check-circle fa-2x text-success"></i>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose Us / Benefits Section */}
      <section 
        id="benefits" 
        data-animate-section="benefits"
        className={`benefits-section py-5 bg-white fade-in-section ${
          visibleSections.has('benefits') ? 'fade-in-visible' : ''
        }`}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-dark mb-4 section-title">
                Why Choose Our Helpdesk?
              </h2>
              <p className="lead text-muted section-subtitle">
                Experience professional support management designed for efficiency and customer satisfaction
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col lg={6} className="mb-4">
              <div className="d-flex h-100">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                    <i className="fas fa-clock fa-2x text-primary"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-4">
                  <h5 className="fw-bold mb-3">24/7 Availability</h5>
                  <p className="text-muted mb-0">
                    Submit tickets anytime, anywhere. Our system is always available, ensuring you can reach out whenever you need assistance, even outside business hours.
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mb-4">
              <div className="d-flex h-100">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 rounded-3 p-3">
                    <i className="fas fa-bolt fa-2x text-success"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-4">
                  <h5 className="fw-bold mb-3">Fast Response Times</h5>
                  <p className="text-muted mb-0">
                    Our average response time is under 2 minutes. Priority tickets get immediate attention, ensuring critical issues are resolved quickly.
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mb-4">
              <div className="d-flex h-100">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 rounded-3 p-3">
                    <i className="fas fa-shield-alt fa-2x text-info"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-4">
                  <h5 className="fw-bold mb-3">Secure & Confidential</h5>
                  <p className="text-muted mb-0">
                    Your data is protected with enterprise-grade security. All communications are encrypted, and we maintain strict confidentiality standards.
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mb-4">
              <div className="d-flex h-100">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 rounded-3 p-3">
                    <i className="fas fa-users fa-2x text-warning"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-4">
                  <h5 className="fw-bold mb-3">Expert Support Team</h5>
                  <p className="text-muted mb-0">
                    Our experienced support agents are trained to handle complex issues. Benefit from their expertise and industry knowledge to solve your problems effectively.
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mb-4">
              <div className="d-flex h-100">
                <div className="flex-shrink-0">
                  <div className="bg-danger bg-opacity-10 rounded-3 p-3">
                    <i className="fas fa-chart-bar fa-2x text-danger"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-4">
                  <h5 className="fw-bold mb-3">Comprehensive Analytics</h5>
                  <p className="text-muted mb-0">
                    Track your support requests with detailed analytics. Monitor response times, resolution rates, and satisfaction metrics in real-time dashboards.
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mb-4">
              <div className="d-flex h-100">
                <div className="flex-shrink-0">
                  <div className="bg-secondary bg-opacity-10 rounded-3 p-3">
                    <i className="fas fa-sync-alt fa-2x text-secondary"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-4">
                  <h5 className="fw-bold mb-3">SLA Guarantees</h5>
                  <p className="text-muted mb-0">
                    Service Level Agreements ensure committed response and resolution times. Track SLA compliance and receive notifications for any potential breaches.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trust Indicators / Stats Section */}
      <section 
        data-animate-section="trust"
        className={`trust-section py-4 text-white fade-in-section ${
          visibleSections.has('trust') ? 'fade-in-visible' : ''
        }`}
      >
        <Container>
          <Row className="text-center mb-4">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-3 section-title">Why Clients Use Our Helpdesk</h2>
              <p className="lead text-white-50 section-subtitle">
                A support experience focused on responsiveness, clarity, and dependable service
              </p>
            </Col>
          </Row>
          <Row className="text-center g-4">
            <Col lg={3} md={6}>
              <div className={`trust-stat fade-in-item ${visibleSections.has('trust') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.1s' }}>
                <i className="fas fa-users fa-3x mb-3 text-white-50"></i>
                <h3 className="h2 fw-bold mb-2">Dedicated Team</h3>
                <p className="text-white-50 mb-0">Experienced support professionals ready to help</p>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className={`trust-stat fade-in-item ${visibleSections.has('trust') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.2s' }}>
                <i className="fas fa-ticket-alt fa-3x mb-3 text-white-50"></i>
                <h3 className="h2 fw-bold mb-2">Structured Workflows</h3>
                <p className="text-white-50 mb-0">Clear ticket tracking from submission to resolution</p>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className={`trust-stat fade-in-item ${visibleSections.has('trust') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.3s' }}>
                <i className="fas fa-smile fa-3x mb-3 text-white-50"></i>
                <h3 className="h2 fw-bold mb-2">Client-Focused</h3>
                <p className="text-white-50 mb-0">Communication designed for clear and practical outcomes</p>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className={`trust-stat fade-in-item ${visibleSections.has('trust') ? 'fade-in-visible' : ''}`} style={{ animationDelay: '0.4s' }}>
                <i className="fas fa-clock fa-3x mb-3 text-white-50"></i>
                <h3 className="h2 fw-bold mb-2">Timely Updates</h3>
                <p className="text-white-50 mb-0">Stay informed with progress notifications at each stage</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Articles Section */}
      <section 
        id="articles" 
        data-animate-section="articles"
        className={`articles-section py-5 bg-white fade-in-section ${
          visibleSections.has('articles') ? 'fade-in-visible' : ''
        }`}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-dark mb-4 section-title">
                <i className="fas fa-book me-3 text-primary"></i>
                Articles
              </h2>
              <p className="lead text-muted section-subtitle">
                Browse our helpful articles and find quick answers to common questions.
                Our articles are constantly updated to help you get the most out of our services.
              </p>
            </Col>
          </Row>
          {articles.length > 0 ? (
            <>
              <Row className="g-4">
              {articles.map((article) => (
                <Col key={article.id} lg={4} md={6}>
                  <Card 
                    className="h-100 border-0 shadow-sm cursor-pointer hover-shadow"
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => navigate(`/articles/${article.slug}`)}
                  >
                    <Card.Body className="p-4">
                      {article.category && (
                        <div className="mb-3">
                          <Badge bg="primary">
                            {article.category.icon && (
                              <i className={`${article.category.icon} me-1`}></i>
                            )}
                            {article.category.name}
                          </Badge>
                        </div>
                      )}
                      <h5 className="fw-bold mb-3" style={{ 
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: '3em'
                      }}>
                        {article.title}
                      </h5>
                      {article.excerpt && (
                        <p className="text-muted mb-3" style={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          minHeight: '4.5em'
                        }}>
                          {article.excerpt}
                        </p>
                      )}
                      <div className="d-flex justify-content-between align-items-center text-muted small">
                        <span>
                          <i className="fas fa-eye me-2"></i>
                          {article.views} views
                        </span>
                        <span>
                          <i className="fas fa-thumbs-up me-2 text-success"></i>
                          {article.helpful} helpful
                        </span>
                      </div>
                      <div className="mt-3">
                        <span className="text-primary small fw-bold">
                          Read more <i className="fas fa-arrow-right ms-1"></i>
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              </Row>
              <Row className="mt-5">
                <Col className="text-center">
                  <Button 
                    variant="outline-primary" 
                    size="lg"
                    onClick={() => navigate('/articles')}
                  >
                    <i className="fas fa-book-open me-2"></i>
                    Browse All Articles
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <Row>
              <Col className="text-center py-5">
                <i className="fas fa-book-open fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">No articles available yet</h5>
                <p className="text-muted">Check back soon for helpful articles and guides.</p>
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {/* Getting Started / CTA Section */}
      <section 
        id="contact" 
        data-animate-section="contact"
        className={`cta-section py-4 bg-gradient fade-in-section ${
          visibleSections.has('contact') ? 'fade-in-visible' : ''
        }`}
      >
        <Container>
          <Row className="text-center align-items-center">
            <Col lg={8} className="mx-auto text-white">
              <h2 className="display-5 fw-bold mb-4 section-title">
                Ready to Get Started?
              </h2>
              <p className="lead mb-4 text-white-50 section-subtitle">
                Join thousands of satisfied customers who trust our helpdesk solution. 
                Get professional support management today and experience the difference.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-4">
                <Button 
                  variant="warning" 
                  size="lg" 
                  className="px-5 py-3 fw-bold text-dark shadow"
                  onClick={() => navigate('/login')}
                >
                  <i className="fas fa-rocket me-2"></i>
                  Get Started Now
                </Button>
                <Button 
                  variant="light" 
                  size="lg" 
                  className="px-5 py-3 fw-bold text-dark shadow border border-2 border-dark-subtle"
                  onClick={() => navigate('/contact-support')}
                >
                  <i className="fas fa-envelope me-2"></i>
                  Contact Support
                </Button>
              </div>
              <div className="d-flex flex-wrap justify-content-center gap-4 text-white-50 small">
                <div className="d-flex align-items-center">
                  <i className="fas fa-check-circle me-2 text-white"></i>
                  No credit card required
                </div>
                <div className="d-flex align-items-center">
                  <i className="fas fa-check-circle me-2 text-white"></i>
                  Free trial available
                </div>
                <div className="d-flex align-items-center">
                  <i className="fas fa-check-circle me-2 text-white"></i>
                  Setup in minutes
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer
        className="text-light py-5"
        style={{ background: 'linear-gradient(135deg, #6c757d 0%, #212529 100%)' }}
      >
        <Container>
          <Row>
            <Col lg={4} md={6} className="mb-4">
              <h5 className="fw-bold mb-3 text-primary">
                <i className="fas fa-headset me-2"></i>
                AACore Helpdesk
              </h5>
              <p className="text-muted">
                Professional helpdesk solution designed specifically for SaaS companies 
                to manage customer support tickets and deliver exceptional user experiences.
              </p>
              <div className="d-flex gap-3">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => window.open('https://twitter.com', '_blank')}
                  title="Follow us on Twitter"
                >
                  <i className="fab fa-twitter"></i>
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => window.open('https://linkedin.com', '_blank')}
                  title="Follow us on LinkedIn"
                >
                  <i className="fab fa-linkedin"></i>
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => window.open('https://github.com', '_blank')}
                  title="View on GitHub"
                >
                  <i className="fab fa-github"></i>
                </Button>
              </div>
            </Col>
            <Col lg={2} md={6} sm={6} className="mb-4">
              <h6 className="fw-bold mb-3">Product</h6>
              <ul className="list-unstyled">
                <li>
                  <a 
                    href="#features" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      const featuresSection = document.getElementById('features');
                      if (featuresSection) {
                        featuresSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a 
                    href="#articles" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      const articlesSection = document.getElementById('articles');
                      if (articlesSection) {
                        articlesSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Articles
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/articles');
                    }}
                  >
                    Knowledge Base
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/sla-info');
                    }}
                  >
                    SLA Info
                  </a>
                </li>
              </ul>
            </Col>
            <Col lg={2} md={6} sm={6} className="mb-4">
              <h6 className="fw-bold mb-3">Company</h6>
              <ul className="list-unstyled">
                <li>
                  <a 
                    href="#hero" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    About
                  </a>
                </li>
                <li>
                  <a 
                    href="#articles" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      const articlesSection = document.getElementById('articles');
                      if (articlesSection) {
                        articlesSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <a 
                    href="#contact" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        navigate('/contact-support');
                      }
                    }}
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Back to Top
                  </a>
                </li>
              </ul>
            </Col>
            <Col lg={2} md={6} sm={6} className="mb-4">
              <h6 className="fw-bold mb-3">Support</h6>
              <ul className="list-unstyled">
                <li>
                  <a 
                    href="#articles" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      const articlesSection = document.getElementById('articles');
                      if (articlesSection) {
                        articlesSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Help Center
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/articles');
                    }}
                  >
                    Documentation
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/contact-support');
                    }}
                  >
                    Contact Support
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/sla-info');
                    }}
                  >
                    SLA Information
                  </a>
                </li>
              </ul>
            </Col>
            <Col lg={2} md={6} sm={6} className="mb-4">
              <h6 className="fw-bold mb-3">Main Sites</h6>
              <ul className="list-unstyled">
                <li>
                  <a
                    href="https://aainomatic.com"
                    className="text-muted text-decoration-none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    AAInomatic.com
                  </a>
                </li>
                <li>
                  <a
                    href="https://aacore.net"
                    className="text-muted text-decoration-none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    AACore.net
                  </a>
                </li>
              </ul>
            </Col>
            <Col lg={2} md={6} sm={6} className="mb-4">
              <h6 className="fw-bold mb-3">Legal</h6>
              <ul className="list-unstyled">
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      // Add privacy policy page or link when available
                      console.log('Privacy policy');
                    }}
                  >
                    Privacy
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      // Add terms page or link when available
                      console.log('Terms of service');
                    }}
                  >
                    Terms
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/contact-support');
                    }}
                  >
                    Security
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="text-muted text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/contact-support');
                    }}
                  >
                    GDPR
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
          <hr className="my-4" />
          <Row className="align-items-center">
            <Col md={6}>
              <p className="text-muted mb-0">
                © {new Date().getFullYear()} AACore Helpdesk. All rights reserved.
              </p>
            </Col>
            <Col md={6} className="text-md-end"></Col>
          </Row>
        </Container>
      </footer>

      {/* ChatBot */}
      <ChatBot isOpen={isChatOpen} onToggle={() => setIsChatOpen(!isChatOpen)} />
    </div>
  );
};
